import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useEvent } from 'expo';
import { useVideoPlayer, VideoView } from 'expo-video';
import { Platform, StyleSheet, View } from 'react-native';
import { Pressable } from 'react-native-gesture-handler';
import Animated, { useAnimatedStyle } from 'react-native-reanimated';

import { ProductCardConstants } from '@garnish/commerce';
import { theme } from '@garnish/constants';
import { FadeView, LoadingDots } from '@garnish/core';
import { addHttpsProtocolToURL, ContentfulImage } from '@garnish/media';
import { useRemoteAsset } from '@garnish/remote-asset';

import { type ProductDetailsCardCarouselItem } from '../../ProductDetailsCardCarousel.types';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsCardCarouselSlide = memo((props: Props) => {
  const { item, slideWidth, slideGap, isLast, isActive, onPress } = props;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerAnimatedStyles = useAnimatedStyle(() => {
    return {
      width: slideWidth,
      marginRight: isLast ? slideGap : 0,
      marginLeft: slideGap,
    };
  });

  const containerStyles = [styles.slide, containerAnimatedStyles];

  // ─────────────────────────────────────────────────────────────────────

  if (onPress) {
    return (
      <Animated.View style={containerStyles} aria-selected={isActive}>
        <Pressable style={styles.slidePressable} onPress={onPress}>
          <ProductDetailsCardCarouselSlideMedia item={item} isActive={isActive} />
        </Pressable>
      </Animated.View>
    );
  }

  return (
    <Animated.View style={containerStyles} aria-selected={isActive}>
      <ProductDetailsCardCarouselSlideMedia item={item} isActive={isActive} />
    </Animated.View>
  );
});

// ─────────────────────────────────────────────────────────────────────────────

const ProductDetailsCardCarouselSlideMedia = memo((props: MediaProps) => {
  const { item, isActive } = props;

  if (item.type === 'image') {
    return (
      <ContentfulImage
        baseUrl={item.source}
        autoplay={Platform.OS === 'ios' ? isActive : undefined} // NOTE: Autoplay is not stable on Android
        staticBaseUrl={item.staticSource}
        options={ProductCardConstants.IMAGE_TRANSFORM_CONFIG_CONTENTFUL}
        alt={item.alt}
        style={styles.media}
        contentFit={item.contentFit ?? 'cover'}
        withLoadingSkeleton
      />
    );
  }

  return (
    <ProductDetailsCardCarouselSlideVideo
      url={item.source}
      isActive={isActive}
      staticImageUrl={item.staticSource}
    />
  );
});

// ─────────────────────────────────────────────────────────────────────────────

const ProductDetailsCardCarouselSlideVideo = memo((props: VideoProps) => {
  const { url, isActive, staticImageUrl } = props;

  // ─────────────────────────────────────────────────────────────────────

  const formattedURL = addHttpsProtocolToURL(url);
  const { localURI: cachedVideoURI = null } = useRemoteAsset({ url: formattedURL }); // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  if (!cachedVideoURI) {
    return (
      <View style={styles.mediaContainer}>
        <LoadingDots color={theme.colors.DARK_KALE} />
      </View>
    );
  }

  return (
    <View
      style={styles.mediaContainer}
      {...(staticImageUrl ? { dataSet: { staticSource: staticImageUrl } } : {})}
    >
      <ProductDetailsCardCarouselSlideVideoReady url={cachedVideoURI} isActive={isActive} />
    </View>
  );
});

const ProductDetailsCardCarouselSlideVideoReady = (props: VideoReadyProps) => {
  const { url, isActive } = props;

  const player = useVideoPlayer(url, (player) => {
    player.loop = true;
    player.muted = true;
  });

  const { status } = useEvent(player, 'statusChange', { status: player.status }); // prettier-ignore

  const isLoading = status !== 'readyToPlay';

  // ─── Refs ────────────────────────────────────────────────────────────

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const togglePlay = useCallback(() => {
    if (!isActive) {
      player.pause();

      return;
    }

    player.replay();
    player.play(); // NOTE: For some reason, the video doesn't play on iOS without this
  }, [isActive, player]);

  /**
   * Disables looping the video after timeout to prevent having long running videos
   * to save data and battery.
   */
  const pauseAfterTimeout = useCallback(() => {
    if (!isActive) return;

    timeoutRef.current = setTimeout(() => {
      player.loop = false;
    }, 60 * 1000); // 1 minute

    // Cleanup
    return () => {
      player.loop = true;

      if (!timeoutRef.current) return;

      clearTimeout(timeoutRef.current);
    };
  }, [isActive, player]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(togglePlay, [togglePlay]);
  useEffect(pauseAfterTimeout, [pauseAfterTimeout]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.mediaContainer}>
      <VideoView player={player} contentFit="cover" style={styles.media} nativeControls={false} />

      <FadeView show={isLoading} style={StyleSheet.absoluteFill}>
        <LoadingDots color={theme.colors.DARK_KALE} />
      </FadeView>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  slide: {
    height: ProductCardConstants.IMAGE_SIZE,
    backgroundColor: theme.colors.OPACITY.BLACK.ALMOST_TRANSPARENT,
    borderRadius: theme.radius.large,
    overflow: 'hidden',
  },
  mediaContainer: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  media: {
    width: '100%',
    height: '100%',
  },
  slidePressable: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  item: ProductDetailsCardCarouselItem;
  slideWidth: number;
  slideGap: number;
  isActive: boolean;
  isLast: boolean;
  onPress?: () => void;
};

type MediaProps = {
  item: ProductDetailsCardCarouselItem;
  isActive: boolean;
};

type VideoProps = {
  url: string;
  isActive: boolean;
  staticImageUrl?: string;
};

type VideoReadyProps = {
  url: string;
  isActive: boolean;
};
